import { ArrowLeft } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { errorMessage, isApiError } from '@/api/client';
import { useAlerts, useDashboard, useSaveAlert } from '@/api/hooks';
import type { Alert, AlertCondition, RangeKey } from '@/api/types';
import { useToast } from '@/components/toast-context';
import { Button, Card, ErrorNote, PageHeader, SelectField, Skeleton, Switch, TextField } from '@/components/ui';
import { readRange } from '@/features/dashboard/range';
import { DestinationPicker } from '@/features/destinations/DestinationPicker';
import { NotFound } from '@/features/NotFound';
import { currencyPrefix, formatKpi } from '@/lib/format';
import { CONDITIONS, conditionText, isPct, WINDOW_LABEL } from './alertText';

interface Draft {
  widget_id: string;
  condition: AlertCondition;
  threshold: string;
  window: RangeKey;
  destination_ids: string[];
  cooldown_hours: string;
  enabled: boolean;
}

export function AlertForm() {
  const { alertId } = useParams();
  const alerts = useAlerts();
  const [params] = useSearchParams();
  const existing = alerts.data?.find((a) => a.id === alertId);
  if (alertId && alerts.isPending) return <Skeleton className="h-96" />;
  if (alertId && alerts.data && !existing) return <NotFound />;
  return <Form key={existing?.id ?? 'new'} existing={existing} presetWidget={params.get('widget') ?? ''} />;
}

function Form({ existing, presetWidget }: { existing?: Alert; presetWidget: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const save = useSaveAlert();
  const dash = useDashboard(readRange());
  const widgets = (dash.data?.widgets ?? []).filter((w) => w.unit !== 'text');
  const [d, setD] = useState<Draft>(() => ({
    widget_id: existing?.widget_id ?? presetWidget,
    condition: existing?.condition ?? 'change_pct',
    threshold: existing ? String(existing.threshold) : '10',
    window: existing?.window ?? '7d',
    destination_ids: existing?.destination_ids ?? [],
    cooldown_hours: String(existing?.cooldown_hours ?? 24),
    enabled: existing?.enabled ?? true,
  }));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((x) => ({ ...x, [k]: v }));
  const widget = widgets.find((w) => w.id === d.widget_id);
  const pct = isPct(d.condition);
  const threshold = Number(d.threshold);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const f: Record<string, string> = {};
    if (!d.widget_id) f.widget_id = 'Pick a KPI.';
    if (d.threshold.trim() === '' || Number.isNaN(threshold)) f.threshold = 'Enter a number.';
    else if (pct && threshold <= 0) f.threshold = 'Use a percentage above 0.';
    const cooldown = Number(d.cooldown_hours);
    if (!Number.isInteger(cooldown) || cooldown < 1) f.cooldown_hours = 'At least 1 hour.';
    if (!d.destination_ids.length) f.destination_ids = 'Pick at least one place to send it.';
    setErrors(f);
    if (Object.keys(f).length) return;
    setServerError(null);
    try {
      const a = await save.mutateAsync({
        id: existing?.id,
        input: {
          widget_id: d.widget_id,
          condition: d.condition,
          threshold,
          window: d.window,
          destination_ids: d.destination_ids,
          cooldown_hours: cooldown,
          enabled: d.enabled,
        },
      });
      toast({ title: existing ? 'Alert saved' : 'Alert created', description: a.label });
      navigate('/alerts');
    } catch (err) {
      if (isApiError(err)) setErrors(err.fields);
      setServerError(errorMessage(err));
    }
  }

  const unitHint = widget
    ? widget.unit === 'currency'
      ? `In ${widget.currency ?? 'your currency'} (${currencyPrefix(widget.currency).trim()})`
      : widget.unit === 'percent'
        ? 'A percentage value, e.g. 2.5'
        : widget.unit === 'duration'
          ? 'In seconds'
          : undefined
    : undefined;

  return (
    <div className="mx-auto max-w-2xl">
      <Link to="/alerts" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-ink hover:text-ink">
        <ArrowLeft className="size-4" aria-hidden /> Alerts
      </Link>
      <PageHeader title={existing ? 'Edit alert' : 'New alert'} />
      <form onSubmit={submit} noValidate>
        <Card className="flex flex-col gap-5 p-5">
          {serverError && <ErrorNote message={serverError} />}
          {dash.isPending ? (
            <Skeleton className="h-16" />
          ) : (
            <SelectField label="KPI" value={d.widget_id} onChange={(e) => set('widget_id', e.target.value)} error={errors.widget_id} hint="Alerts watch KPIs that are on your dashboard.">
              <option value="">Choose a KPI…</option>
              {widgets.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.integration_name} · {w.label} ({w.connection_name})
                </option>
              ))}
            </SelectField>
          )}
          <div className="grid gap-4 sm:grid-cols-[1fr_180px]">
            <SelectField label="When it" value={d.condition} onChange={(e) => set('condition', e.target.value as AlertCondition)}>
              {CONDITIONS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </SelectField>
            <TextField
              label={pct ? 'Percent' : 'Value'}
              type="number"
              inputMode="decimal"
              step="any"
              min={pct ? 0 : undefined}
              value={d.threshold}
              onChange={(e) => set('threshold', e.target.value)}
              error={errors.threshold}
              hint={pct ? '%' : unitHint}
            />
          </div>
          <SelectField label="Compare" value={d.window} onChange={(e) => set('window', e.target.value as RangeKey)} hint={pct ? 'The change is measured against the previous equivalent period.' : 'The KPI’s value for this period.'}>
            {(['today', '7d', '30d'] as const).map((w) => (
              <option key={w} value={w}>
                {w === 'today' ? 'Today' : w === '7d' ? 'Last 7 days' : 'Last 30 days'} ({WINDOW_LABEL[w]})
              </option>
            ))}
          </SelectField>
          <DestinationPicker value={d.destination_ids} onChange={(ids) => set('destination_ids', ids)} error={errors.destination_ids} />
          <TextField
            label="Quiet period (hours)"
            type="number"
            min={1}
            step={1}
            value={d.cooldown_hours}
            onChange={(e) => set('cooldown_hours', e.target.value)}
            error={errors.cooldown_hours}
            hint="After it fires, don’t send it again for this long."
            fieldClassName="max-w-xs"
          />
          <Switch label="Alert is on" checked={d.enabled} onChange={(v) => set('enabled', v)} />
          {widget && !Number.isNaN(threshold) && (
            <p className="rounded-control bg-muted px-3 py-2 text-sm" aria-live="polite">
              You’ll hear about it when <strong>{widget.integration_name} {widget.label}</strong>{' '}
              {conditionText(d.condition, threshold, formatKpi(threshold, widget.unit, widget.currency))} ({WINDOW_LABEL[d.window]}).
            </p>
          )}
        </Card>
        <div className="mt-4 flex justify-end gap-2">
          <Button onClick={() => navigate('/alerts')}>Cancel</Button>
          <Button type="submit" variant="primary" loading={save.isPending}>
            {existing ? 'Save alert' : 'Create alert'}
          </Button>
        </div>
      </form>
    </div>
  );
}
